<script setup lang="ts">
import {computed, onMounted} from 'vue';
import {storeToRefs} from 'pinia';
import {useCrmMetadataStore} from '@/stores';
import {readField} from '@/crm/odata';
import type {EntityName, FieldDef} from '@/crm/spec/types';

interface IProps {
    title: string;
    entity: EntityName;
    columns: FieldDef[];
    rows: any[];
    showCount?: boolean;
    emptyMessage?: string;
}

const props = withDefaults(defineProps<IProps>(), {
    showCount: true,
    emptyMessage: 'No records',
});

const metadataStore = useCrmMetadataStore();
const {entities} = storeToRefs(metadataStore);

onMounted(() => {
    metadataStore.ensureEntity(props.entity);
});

function header(def: FieldDef): string {
    if (def.labelOverride) return def.labelOverride;
    void entities.value;
    return metadataStore.getLabel(props.entity, def.field);
}

function cell(row: any, def: FieldDef): string {
    return readField(row, def.field) ?? '-';
}

const isEmpty = computed(() => !props.rows || props.rows.length === 0);
</script>

<template>
    <section class="simple-table">
        <h2>{{ title }}</h2>
        <div v-if="isEmpty" class="empty">{{ emptyMessage }}</div>
        <table v-else class="table">
            <thead>
                <tr>
                    <th v-for="(c, i) in columns" :key="i">{{ header(c) }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, ri) in rows" :key="ri">
                    <td v-for="(c, ci) in columns" :key="ci" :class="{empty: cell(row, c) === '-'}">
                        {{ cell(row, c) }}
                    </td>
                </tr>
            </tbody>
        </table>
        <div v-if="showCount && !isEmpty" class="footer">Rows: {{ rows.length }}</div>
    </section>
</template>

<style scoped>
.simple-table {
    background: var(--color-background-bright);
    border: 1px solid var(--color-border, rgba(0, 0, 0, 0.08));
    border-radius: 0.5rem;
    padding: 1rem 1.25rem 1.25rem;
    margin-bottom: 1rem;
}

.simple-table h2 {
    margin: 0 0 0.75rem;
    font-size: var(--font-size-h2, 1.1rem);
    font-weight: 600;
    color: var(--color-text);
}

.empty {
    opacity: 0.6;
    font-size: var(--font-size-body);
    color: var(--color-text);
}

.table {
    width: 100%;
    border-collapse: collapse;
}

.table th,
.table td {
    padding: 0.4rem 0.75rem 0.4rem 0;
    font-size: var(--font-size-body);
    color: var(--color-text);
    border-bottom: 1px solid var(--color-border, rgba(0, 0, 0, 0.06));
    vertical-align: top;
    text-align: left;
}

.table th {
    font-weight: 600;
    opacity: 0.75;
}

.table td.empty {
    opacity: 0.4;
}

.footer {
    margin-top: 0.5rem;
    opacity: 0.6;
    font-size: 0.85rem;
    color: var(--color-text);
}
</style>
